import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, ExternalLink, Ban, QrCode } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/dashboard/PageHeader";

export function PassportPanel() {
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState("");
  const [consenso, setConsenso] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tenants");
    setTenants(data);
  };
  useEffect(() => { load().catch((err) => toast.error(formatApiError(err))); }, []);

  const tenant = tenants.find((t) => t.id === selected);
  const link = tenant?.passport_token ? `${window.location.origin}/passaporto/${tenant.passport_token}` : null;

  const generate = async () => {
    try {
      await api.post(`/tenants/${selected}/passport`, { consenso });
      toast.success("Link del passaporto generato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const revoke = async () => {
    await api.delete(`/tenants/${selected}/passport`);
    toast.success("Passaporto revocato");
    setConsenso(false);
    await load();
  };

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    toast.success("Link copiato negli appunti");
  };

  return (
    <div>
      <PageHeader eyebrow="Passaporto dell'Inquilino" title="Condividi lo storico puntuale" text="Genera un link pubblico con lo score di affidabilità e lo storico dei pagamenti, da condividere con agenzie e locatori." />
      <div className="grid gap-8 xl:grid-cols-[1fr_1fr]">
        <section className="surface space-y-5 p-6 sm:p-8">
          <div>
            <label className="field-label">Inquilino</label>
            <Select value={selected} onValueChange={(v) => { setSelected(v); setConsenso(false); }}>
              <SelectTrigger data-testid="select-passport-tenant"><SelectValue placeholder={tenants.length ? "Seleziona un inquilino" : "Nessun inquilino registrato"} /></SelectTrigger>
              <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`passport-tenant-option-${t.id}`}>{t.nome} {t.cognome} · {t.mesi_pagati} mesi registrati</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {tenant && !link && (
            <>
              <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">
                <Checkbox data-testid="passport-checkbox-consenso" checked={consenso} onCheckedChange={(v) => setConsenso(!!v)} className="mt-0.5" />
                Confermo di aver ricevuto il consenso esplicito dell'inquilino alla condivisione del proprio storico pagamenti.
              </label>
              <button data-testid="button-generate-passport-link" disabled={!consenso} onClick={generate} className="btn-accent w-full"><QrCode className="h-4 w-4" />Genera link del passaporto</button>
            </>
          )}
          {link && (
            <div className="space-y-3">
              <label className="field-label">Link pubblico</label>
              <div className="flex gap-2">
                <input data-testid="passport-link-input" readOnly value={link} className="field-input font-mono text-xs" />
                <button data-testid="button-copy-passport-link" onClick={copy} className="btn-ghost"><Copy className="h-4 w-4" /></button>
              </div>
              <div className="flex flex-wrap gap-2">
                <a data-testid="button-open-passport" href={link} target="_blank" rel="noreferrer" className="btn-ghost"><ExternalLink className="h-4 w-4" />Apri anteprima</a>
                <button data-testid="button-revoke-passport" onClick={revoke} className="btn-ghost hover:!border-[#dc2626] hover:!text-[#dc2626]"><Ban className="h-4 w-4" />Revoca</button>
              </div>
            </div>
          )}
        </section>
        <section className="flex min-h-[280px] items-center justify-center rounded-2xl border border-slate-200 bg-[#172554] p-8 text-white grain relative overflow-hidden">
          {link ? (
            <div className="relative text-center" data-testid="passport-qr">
              <div className="inline-block rounded-2xl bg-white p-4"><QRCodeSVG value={link} size={180} fgColor="#172554" /></div>
              <p className="mt-5 font-display text-xl font-bold">{tenant.nome} {tenant.cognome[0]}.</p>
              <p className="text-sm text-slate-300">Scansiona per verificare il passaporto</p>
            </div>
          ) : (
            <p className="relative text-center text-sm text-slate-300">Seleziona un inquilino e genera il link per visualizzare il codice QR.</p>
          )}
        </section>
      </div>
    </div>
  );
}
